import { authFetch, clearToken } from "../utils/auth";
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { 
  Target, Users, TrendingUp, Clock, Plus, BookOpen, 
  BarChart3, Settings, AlertTriangle, ChevronRight,
  Mail, UserPlus, Dumbbell
} from "lucide-react";
import { toast } from "sonner";
import { motion } from "framer-motion";

const API = window.location.origin;

export default function CoachDashboard() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [dashboard, setDashboard] = useState(null);
  const [recentStudents, setRecentStudents] = useState([]);

  // eslint-disable-next-line react-hooks/exhaustive-deps -- Mount-only
  useEffect(() => {
    fetchDashboard();
    fetchStudents();
  }, []);

  const fetchDashboard = async () => {
    try {
      const response = await authFetch(`${API}/api/coach/dashboard`, {
      });
      if (response.ok) {
        const data = await response.json();
        setDashboard(data);
      } else if (response.status === 403) {
        // Not a coach, redirect
        navigate("/dashboard");
      }
    } catch (error) {
      console.error("Error fetching dashboard:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchStudents = async () => {
    try {
      const response = await authFetch(`${API}/api/coach/students`, {
      });
      if (response.ok) {
        const data = await response.json();
        setRecentStudents(data.students?.slice(0, 5) || []);
      }
    } catch (error) {
      console.error("Error fetching students:", error);
    }
  };

  const generateInvite = async () => {
    try {
      const response = await authFetch(`${API}/api/coach/students/invite`, {
        method: "POST",
      });
      if (response.ok) {
        const data = await response.json();
        navigator.clipboard.writeText(data.invite_url);
        toast.success("Invite link copied to clipboard!");
      }
    } catch (error) {
      toast.error("Failed to generate invite link");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="w-10 h-10 border-2 border-emerald-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="px-6 py-4 border-b border-gray-200">
        <div className="max-w-6xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-emerald-500 flex items-center justify-center">
              <Target className="w-5 h-5 text-[#111]" />
            </div>
            <div>
              <span className="font-bold text-lg text-[#111]">GolfFlowPro</span>
              <Badge className="ml-2 bg-purple-500/20 text-purple-400 border-purple-500/30">Coach</Badge>
            </div>
          </div>
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate("/coach/settings")}
            className="text-[#555] hover:text-[#111]"
          >
            <Settings className="w-5 h-5" />
          </Button>
        </div>
      </header>

      {/* Navigation Tabs */}
      <nav className="px-6 py-3 border-b border-gray-200 bg-gray-50">
        <div className="max-w-6xl mx-auto flex gap-1">
          {[
            { id: "dashboard", label: "Dashboard", icon: <Target className="w-4 h-4" />, path: "/coach" },
            { id: "students", label: "Students", icon: <Users className="w-4 h-4" />, path: "/coach/students" },
            { id: "library", label: "Drill Library", icon: <BookOpen className="w-4 h-4" />, path: "/coach/library" },
            { id: "analytics", label: "Analytics", icon: <BarChart3 className="w-4 h-4" />, path: "/coach/analytics" }
          ].map((tab) => (
            <Button
              key={tab.id}
              variant="ghost"
              onClick={() => navigate(tab.path)}
              className={`flex items-center gap-2 ${
                tab.id === "dashboard" ? "bg-gray-200 text-[#111]" : "text-[#555] hover:text-[#111]"
              }`}
            >
              {tab.icon}
              {tab.label}
            </Button>
          ))}
        </div>
      </nav>

      <main className="px-6 py-8">
        <div className="max-w-6xl mx-auto space-y-8">
          {/* Welcome */}
          <div>
            <h1 className="text-2xl font-bold text-[#111] mb-1">
              Welcome back, Coach {dashboard?.coach_name?.split(' ')[0] || ''} 👋
            </h1>
            <p className="text-[#555]">Here's how your squad is doing this week</p>
          </div>

          {/* Summary Cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0 }}>
              <Card className="bg-white border-gray-200">
                <CardContent className="p-5">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 rounded-xl bg-emerald-500/20 flex items-center justify-center">
                      <Users className="w-5 h-5 text-emerald-700" />
                    </div>
                  </div>
                  <p className="text-3xl font-bold text-[#111]">{dashboard?.active_students || 0}</p>
                  <p className="text-[#555] text-sm">Active Students</p>
                </CardContent>
              </Card>
            </motion.div>

            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}>
              <Card className="bg-white border-gray-200">
                <CardContent className="p-5">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 rounded-xl bg-blue-500/20 flex items-center justify-center">
                      <TrendingUp className="w-5 h-5 text-blue-400" />
                    </div>
                  </div>
                  <p className="text-3xl font-bold text-[#111]">{dashboard?.avg_compliance || 0}%</p>
                  <p className="text-[#555] text-sm">Avg Compliance</p>
                </CardContent>
              </Card>
            </motion.div>

            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}>
              <Card className={`bg-white border-gray-200 ${dashboard?.students_needing_attention > 0 ? 'border-amber-500/30' : ''}`}>
                <CardContent className="p-5">
                  <div className="flex items-center gap-3 mb-3">
                    <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${
                      dashboard?.students_needing_attention > 0 ? 'bg-amber-500/20' : 'bg-gray-500/20'
                    }`}>
                      <AlertTriangle className={`w-5 h-5 ${
                        dashboard?.students_needing_attention > 0 ? 'text-[#111]' : 'text-[#555]'
                      }`} />
                    </div>
                  </div>
                  <p className="text-3xl font-bold text-[#111]">{dashboard?.students_needing_attention || 0}</p>
                  <p className="text-[#555] text-sm">Need Attention</p>
                </CardContent>
              </Card>
            </motion.div>

            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }}>
              <Card className="bg-white border-gray-200">
                <CardContent className="p-5">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 rounded-xl bg-purple-500/20 flex items-center justify-center">
                      <Clock className="w-5 h-5 text-purple-400" />
                    </div>
                  </div>
                  <p className="text-3xl font-bold text-[#111]">{dashboard?.total_practice_hours || 0}</p>
                  <p className="text-[#555] text-sm">Hours This Week</p>
                </CardContent>
              </Card>
            </motion.div>
          </div>

          {/* Quick Actions */}
          <div>
            <h2 className="text-lg font-semibold text-[#111] mb-4">Quick Actions</h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <Button
                onClick={generateInvite}
                className="h-auto py-4 bg-white border border-gray-200 hover:bg-gray-100 text-[#111] flex flex-col items-center gap-2"
              >
                <UserPlus className="w-5 h-5 text-emerald-700" />
                <span>Add Student</span>
              </Button>
              <Button
                onClick={() => navigate("/coach/library")}
                className="h-auto py-4 bg-white border border-gray-200 hover:bg-gray-100 text-[#111] flex flex-col items-center gap-2"
              >
                <Dumbbell className="w-5 h-5 text-blue-400" />
                <span>Assign Drill</span>
              </Button>
              <Button
                onClick={() => navigate("/coach/library")}
                className="h-auto py-4 bg-white border border-gray-200 hover:bg-gray-100 text-[#111] flex flex-col items-center gap-2"
              >
                <Plus className="w-5 h-5 text-purple-400" />
                <span>Create Drill</span>
              </Button>
              <Button
                onClick={() => toast.info("Messaging coming soon!")}
                className="h-auto py-4 bg-white border border-gray-200 hover:bg-gray-100 text-[#111] flex flex-col items-center gap-2"
              >
                <Mail className="w-5 h-5 text-[#111]" />
                <span>Message</span>
              </Button>
            </div>
          </div>

          {/* Recent Students */}
          <div>
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-[#111]">Recent Students</h2>
              <Button
                variant="ghost"
                onClick={() => navigate("/coach/students")}
                className="text-emerald-700 hover:text-emerald-700"
              >
                View All
                <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            </div>

            {recentStudents.length === 0 ? (
              <Card className="bg-white border-gray-200">
                <CardContent className="p-8 text-center">
                  <div className="w-16 h-16 rounded-full bg-emerald-500/10 flex items-center justify-center mx-auto mb-4">
                    <Users className="w-8 h-8 text-emerald-700" />
                  </div>
                  <h3 className="text-lg font-semibold text-[#111] mb-2">No Students Yet</h3>
                  <p className="text-[#555] mb-4">Generate an invite link to add your first student</p>
                  <Button onClick={generateInvite} className="bg-emerald-500 hover:bg-emerald-600 text-white">
                    <UserPlus className="w-4 h-4 mr-2" />
                    Generate Invite Link
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-2">
                {recentStudents.map((student) => (
                  <Card
                    key={student.student_id}
                    className="bg-white border-gray-200 hover:border-white/20 cursor-pointer transition-colors"
                    onClick={() => navigate(`/coach/students/${student.student_id}`)}
                  >
                    <CardContent className="p-4 flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className="w-10 h-10 rounded-full bg-emerald-500/20 flex items-center justify-center text-emerald-700 font-semibold">
                          {student.name?.charAt(0) || "?"}
                        </div>
                        <div>
                          <p className="font-medium text-[#111]">{student.name}</p>
                          <p className="text-sm text-[#555]">
                            {student.handicap ? `HCP ${student.handicap}` : "No handicap"} • 
                            {student.sessions_this_week}/{student.expected_sessions} sessions
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <div className={`text-right ${
                          student.compliance >= 75 ? 'text-emerald-700' :
                          student.compliance >= 50 ? 'text-[#111]' : 'text-red-400'
                        }`}>
                          <p className="font-bold">{student.compliance}%</p>
                          <p className="text-xs text-[#555]">compliance</p>
                        </div>
                        <ChevronRight className="w-5 h-5 text-[#555]" />
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}
